import React from 'react';
import { Flex, Button } from '@chakra-ui/react';

import {
  Modal,
  InputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  CurrencyInputWithConfirm,
  PercentageInputWithConfirm
} from 'shared-components';
import * as types from 'types';

import { OrganisationSponsorship, ConfigField, FundingSummary } from '../../../../types';

import FundingCoverSelect from '../../../atoms/funding-cover-select';
import { getPageStatus, internationalOrgs } from './utils';
import { coversCourses, coversLiving } from '../../../../utilities/sponsors';

interface IProps {
  sponsorKey: string | null;
  id: string;
  fundingSummary: FundingSummary;
  saveButtonText: string;
  config: any;
  onSave: (data: OrganisationSponsorship) => void;
  onClose: () => void;
}

type IComponentProps = Partial<OrganisationSponsorship> & IProps;

interface IState {
  orgType: string;
  isMultiCountry?: boolean;
  sponsorName: string;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;

  sponsorNameError: string;
  coursePercentageError: string;
  courseAmountError: string;
  livingPercentageError: string;
  livingAmountError: string;
  courseAndLivingPercentageError: string;
  courseAndLivingAmountError: string;
}

class OrganisationSponsor extends React.Component<IComponentProps, IState> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      orgType: props.orgType || '',
      isMultiCountry: props.isMultiCountry,
      sponsorName: props.sponsorName || '',
      sponsorshipCovers: props.sponsorshipCovers || [],
      courseFundType: props.courseFundType || '',
      courseFundPercentage: props.courseFundPercentage || 0,
      courseFundAmount: props.courseFundAmount || 0,
      livingFundType: props.livingFundType || '',
      livingFundPercentage: props.livingFundPercentage || 0,
      livingFundAmountType: props.livingFundAmountType || '',
      livingFundAmount: props.livingFundAmount || 0,
      courseAndLivingFundSeparate: props.courseAndLivingFundSeparate,
      courseAndLivingFundType: props.courseAndLivingFundType || '',
      courseAndLivingFundPercentage: props.courseAndLivingFundPercentage || 0,
      courseAndLivingFundAmount: props.courseAndLivingFundAmount || 0,

      sponsorNameError: '',
      coursePercentageError: '',
      courseAmountError: '',
      livingPercentageError: '',
      livingAmountError: '',
      courseAndLivingPercentageError: '',
      courseAndLivingAmountError: ''
    };
  }

  componentDidUpdate(prevProps: IComponentProps, prevState: IState) {
    if (prevState.orgType !== this.state.orgType) {
      if (!internationalOrgs.includes(this.state.orgType)) {
        this.setState({
          isMultiCountry: undefined
        });
      }
    }

    if (
      coversCourses(prevState.sponsorshipCovers) !== coversCourses(this.state.sponsorshipCovers) ||
      coversLiving(prevState.sponsorshipCovers) !== coversLiving(this.state.sponsorshipCovers)
    ) {
      this.setState({
        courseFundType: '',
        courseFundPercentage: 0,
        courseFundAmount: 0,
        livingFundType: '',
        livingFundPercentage: 0,
        livingFundAmountType: '',
        livingFundAmount: 0,
        courseAndLivingFundSeparate: undefined,
        courseAndLivingFundType: '',
        courseAndLivingFundPercentage: 0,
        courseAndLivingFundAmount: 0
      });
    }

    if (prevState.courseAndLivingFundSeparate !== this.state.courseAndLivingFundSeparate) {
      this.setState({
        courseFundType: '',
        courseFundPercentage: 0,
        courseFundAmount: 0,
        livingFundType: '',
        livingFundPercentage: 0,
        livingFundAmountType: '',
        livingFundAmount: 0,
        courseAndLivingFundType: '',
        courseAndLivingFundPercentage: 0,
        courseAndLivingFundAmount: 0
      });
    }

    if (prevState.courseFundType !== this.state.courseFundType) {
      this.setState({
        courseFundPercentage: 0,
        courseFundAmount: 0
      });
    }

    if (prevState.livingFundType !== this.state.livingFundType) {
      this.setState({
        livingFundPercentage: 0,
        livingFundAmountType: '',
        livingFundAmount: 0
      });
    }

    if (prevState.livingFundAmountType !== this.state.livingFundAmountType) {
      this.setState({
        livingFundAmount: 0
      });
    }

    if (prevState.courseAndLivingFundType !== this.state.courseAndLivingFundType) {
      this.setState({
        courseAndLivingFundPercentage: 0,
        courseAndLivingFundAmount: 0
      });
    }
  }

  onOrganisationTypeClick = (question: IQuestion) => {
    this.setState({
      orgType: question.label
    });
  };

  onOrganisationTypeKeyDown = (event: any, question: IQuestion) => {
    if (event.key === ' ' || event.key === 'Enter') {
      this.onOrganisationTypeClick(question);
    }
  };

  onCountryPresenceClick = (question: IQuestion) => {
    this.setState({
      isMultiCountry: question.label === 'Yes'
    });
  };

  onCountryPresenceKeyDown = (event: any, question: IQuestion) => {
    if (event.key === ' ' || event.key === 'Enter') {
      this.onCountryPresenceClick(question);
    }
  };

  getOrganisationTypes = (selectedType: string) => [
    {
      char: '1',
      label: types.OrgType.UK_GOV,
      selected: selectedType === types.OrgType.UK_GOV
    },
    {
      char: '2',
      label: types.OrgType.BRITISH_GOV,
      selected: selectedType === types.OrgType.BRITISH_GOV
    },
    {
      char: '3',
      label: types.OrgType.YOUR_GOV,
      selected: selectedType === types.OrgType.YOUR_GOV
    },
    {
      char: '4',
      label: types.OrgType.INT_ORG,
      selected: selectedType === types.OrgType.INT_ORG
    },
    {
      char: '5',
      label: types.OrgType.INT_COMP,
      selected: selectedType === types.OrgType.INT_COMP
    }
  ];

  onSponsorNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({
      sponsorName: event.currentTarget.value
    });
  };

  getPageStatus = () => {
    return getPageStatus({
      orgType: this.state.orgType,
      isMultiCountry: this.state.isMultiCountry,
      sponsorName: this.state.sponsorName,
      sponsorshipCovers: this.state.sponsorshipCovers,
      courseFundType: this.state.courseFundType,
      courseFundPercentage: this.state.courseFundPercentage,
      courseFundAmount: this.state.courseFundAmount,
      livingFundType: this.state.livingFundType,
      livingFundPercentage: this.state.livingFundPercentage,
      livingFundAmountType: this.state.livingFundAmountType,
      livingFundAmount: this.state.livingFundAmount,
      courseAndLivingFundSeparate: this.state.courseAndLivingFundSeparate,
      courseAndLivingFundType: this.state.courseAndLivingFundType,
      courseAndLivingFundPercentage: this.state.courseAndLivingFundPercentage,
      courseAndLivingFundAmount: this.state.courseAndLivingFundAmount
    });
  };

  onSaveRegionDetails = () => {
    this.props.onSave({
      orgType: this.state.orgType as types.OrgType,
      isMultiCountry: this.state.isMultiCountry,
      sponsorName: this.state.sponsorName,
      sponsorshipCovers: this.state.sponsorshipCovers,
      courseFundType: this.state.courseFundType as OrganisationSponsorship['courseFundType'],
      courseFundPercentage: this.state.courseFundPercentage,
      courseFundAmount: this.state.courseFundAmount,
      livingFundType: this.state.livingFundType as OrganisationSponsorship['livingFundType'],
      livingFundPercentage: this.state.livingFundPercentage,
      livingFundAmountType: this.state.livingFundAmountType as OrganisationSponsorship['livingFundAmountType'],
      livingFundAmount: this.state.livingFundAmount,
      courseAndLivingFundSeparate: this.state.courseAndLivingFundSeparate,
      courseAndLivingFundType: this.state.courseAndLivingFundType as OrganisationSponsorship['courseAndLivingFundType'],
      courseAndLivingFundPercentage: this.state.courseAndLivingFundPercentage,
      courseAndLivingFundAmount: this.state.courseAndLivingFundAmount
    });
    this.props.onClose();
  };

  onSaveRegionKeyPress = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === ' ' || event.key === 'Enter') {
      this.onSaveRegionDetails();
    }
  };

  onCourseTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'The full amount of the above costs':
        value = 'Full';
        break;
      case 'A part percentage of the above costs':
        value = 'Percentage';
        break;
      case 'A specific amount':
        value = 'Amount';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      courseFundType: value
    });
  };

  onLivingTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'The full amount of the above costs':
        value = 'Full';
        break;
      case 'A part percentage of the above costs':
        value = 'Percentage';
        break;
      case 'A specific amount':
        value = 'Amount';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      livingFundType: value
    });
  };

  onLivingAmountTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'Monthly Stipend':
        value = 'Monthly';
        break;
      case 'Upfront':
        value = 'Total';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      livingFundAmountType: value
    });
  };

  onCourseAndLivingTypeChange = (question: IQuestion) => {
    let value = '';
    switch (question.label) {
      case 'The full amount of the above costs':
        value = 'Full';
        break;
      case 'A part percentage of the above costs':
        value = 'Percentage';
        break;
      case 'A specific amount':
        value = 'Amount';
        break;
      default:
        value = '';
        break;
    }
    this.setState({
      courseAndLivingFundType: value
    });
  };

  renderCourse() {
    const {
      CoverType: coverTypeConfig
    }: {
      [key: string]: ConfigField;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <>
        <RadioOptionsWithConfirm
          id="courseType"
          label="How much of the tuition fees does the sponsorship cover?"
          questions={[
            {
              char: '1',
              label: 'The full amount of the above costs',
              selected: this.state.courseFundType === 'Full'
            },
            {
              char: '2',
              label: 'A part percentage of the above costs',
              selected: this.state.courseFundType === 'Percentage'
            },
            {
              char: '3',
              label: 'A specific amount',
              selected: this.state.courseFundType === 'Amount'
            }
          ]}
          inputHelperText={coverTypeConfig?.helpText}
          onClick={this.onCourseTypeChange}
          onKeyDown={(event: any, question: IQuestion) => {
            if (event.key === ' ' || event.key === 'Enter') {
              this.onCourseTypeChange(question);
            }
          }}
        />
        {this.state.courseFundType === 'Percentage' ? (
          <PercentageInputWithConfirm
            id="coursePercentage"
            label="What percentage is being covered?"
            value={this.state.courseFundPercentage}
            errorText={this.state.coursePercentageError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseFundPercentage: value
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  coursePercentageError: 'Please enter a percentage value'
                });
              }
            }}
            onFocus={() => {
              this.setState({
                coursePercentageError: ''
              });
            }}
          />
        ) : null}
        {this.state.courseFundType === 'Amount' ? (
          <CurrencyInputWithConfirm
            id="courseAmount"
            label="What is the amount being covered in pounds?"
            currencySymbol="£"
            value={this.state.courseFundAmount}
            errorText={this.state.courseAmountError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseFundAmount: value
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  courseAmountError: 'Please enter an amount'
                });
              }
            }}
            onFocus={() => {
              this.setState({
                courseAmountError: ''
              });
            }}
          />
        ) : null}
      </>
    );
  }

  renderLiving() {
    const {
      CoverType: coverTypeConfig,
      MonthlyOrUpfrontLivingCost: monthlyOrUpfrontLivingCostConfig,
      TotalLivingCost: totalLivingCostConfig,
      MonthlyLivingCost: monthlyLivingCostConfig
    }: {
      [key: string]: ConfigField;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <>
        <RadioOptionsWithConfirm
          id="livingType"
          label="How much of your living costs does this sponsorship cover?"
          questions={[
            {
              char: '1',
              label: 'The full amount of the above costs',
              selected: this.state.livingFundType === 'Full'
            },
            {
              char: '2',
              label: 'A part percentage of the above costs',
              selected: this.state.livingFundType === 'Percentage'
            },
            {
              char: '3',
              label: 'A specific amount',
              selected: this.state.livingFundType === 'Amount'
            }
          ]}
          inputHelperText={coverTypeConfig?.helpText}
          onClick={this.onLivingTypeChange}
          onKeyDown={(event: any, question: IQuestion) => {
            if (event.key === ' ' || event.key === 'Enter') {
              this.onLivingTypeChange(question);
            }
          }}
        />
        {this.state.livingFundType === 'Percentage' ? (
          <PercentageInputWithConfirm
            id="livingPercentage"
            label="What percentage is being covered?"
            value={this.state.livingFundPercentage}
            errorText={this.state.livingPercentageError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                livingFundPercentage: value
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  livingPercentageError: 'Please enter a percentage value'
                });
              }
            }}
            onFocus={() => {
              this.setState({
                livingPercentageError: ''
              });
            }}
          />
        ) : null}
        {this.state.livingFundType === 'Amount' ? (
          <>
            <RadioOptionsWithConfirm
              id="livingAmount"
              label={monthlyOrUpfrontLivingCostConfig?.label}
              questions={[
                {
                  char: '1',
                  label: 'Monthly Stipend',
                  selected: this.state.livingFundAmountType === 'Monthly'
                },
                {
                  char: '2',
                  label: 'Upfront',
                  selected: this.state.livingFundAmountType === 'Total'
                }
              ]}
              inputHelperText={monthlyOrUpfrontLivingCostConfig?.helpText}
              onClick={this.onLivingAmountTypeChange}
              onKeyDown={(event: any, question: IQuestion) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  this.onLivingAmountTypeChange(question);
                }
              }}
            />
            {this.state.livingFundAmountType === 'Monthly' ? (
              <CurrencyInputWithConfirm
                id="livingMonthlyAmount"
                label={monthlyLivingCostConfig?.label}
                currencySymbol="£"
                value={this.state.livingFundAmount}
                errorText={this.state.livingAmountError}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  const value = Number(event.currentTarget.value) || 0;
                  this.setState({
                    livingFundAmount: value
                  });
                }}
                onFocus={() => {
                  this.setState({
                    livingAmountError: ''
                  });
                }}
                onBlur={(event: React.FormEvent<HTMLInputElement>) => {
                  if (event.currentTarget.value === '') {
                    this.setState({
                      livingAmountError: 'Please enter an amount'
                    });
                  }
                }}
                inputHelperText={monthlyLivingCostConfig?.helpText}
              />
            ) : null}
            {this.state.livingFundAmountType === 'Total' ? (
              <CurrencyInputWithConfirm
                id="livingTotalAmount"
                label={totalLivingCostConfig?.label}
                currencySymbol="£"
                value={this.state.livingFundAmount}
                errorText={this.state.livingAmountError}
                onChange={(event: React.FormEvent<HTMLInputElement>) => {
                  const value = Number(event.currentTarget.value) || 0;
                  this.setState({
                    livingFundAmount: value
                  });
                }}
                onFocus={() => {
                  this.setState({
                    livingAmountError: ''
                  });
                }}
                onBlur={(event: React.FormEvent<HTMLInputElement>) => {
                  if (event.currentTarget.value === '') {
                    this.setState({
                      livingAmountError: 'Please enter an amount'
                    });
                  }
                }}
                inputHelperText={totalLivingCostConfig?.helpText}
              />
            ) : null}
          </>
        ) : null}
      </>
    );
  }

  renderCourseAndLiving() {
    const {
      CoverType: coverTypeConfig
    }: {
      [key: string]: ConfigField;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <>
        <RadioOptionsWithConfirm
          id="courseAndLivingType"
          label="How much of your tuition fees and living costs does this sponsorship cover?"
          questions={[
            {
              char: '1',
              label: 'The full amount of the above costs',
              selected: this.state.courseAndLivingFundType === 'Full'
            },
            {
              char: '2',
              label: 'A part percentage of the above costs',
              selected: this.state.courseAndLivingFundType === 'Percentage'
            },
            {
              char: '3',
              label: 'A specific amount',
              selected: this.state.courseAndLivingFundType === 'Amount'
            }
          ]}
          inputHelperText={coverTypeConfig?.helpText}
          onClick={this.onCourseAndLivingTypeChange}
          onKeyDown={(event: any, question: IQuestion) => {
            if (event.key === ' ' || event.key === 'Enter') {
              this.onCourseAndLivingTypeChange(question);
            }
          }}
        />

        {this.state.courseAndLivingFundType === 'Percentage' ? (
          <PercentageInputWithConfirm
            id="courseAndLivingPercentage"
            label="What percentage is being covered?"
            value={this.state.courseAndLivingFundPercentage}
            errorText={this.state.courseAndLivingPercentageError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseAndLivingFundPercentage: value
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  courseAndLivingPercentageError: 'Please enter a percentage value'
                });
              }
            }}
            onFocus={() => {
              this.setState({
                courseAndLivingPercentageError: ''
              });
            }}
          />
        ) : null}

        {this.state.courseAndLivingFundType === 'Amount' ? (
          <CurrencyInputWithConfirm
            id="courseAndLivingAmount"
            label="What is the amount being covered in pounds?"
            currencySymbol="£"
            value={this.state.courseAndLivingFundAmount}
            errorText={this.state.courseAndLivingAmountError}
            onChange={(event: React.FormEvent<HTMLInputElement>) => {
              const value = Number(event.currentTarget.value) || 0;
              this.setState({
                courseAndLivingFundAmount: value
              });
            }}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  courseAndLivingAmountError: 'Please enter an amount'
                });
              }
            }}
            onFocus={() => {
              this.setState({
                courseAndLivingAmountError: ''
              });
            }}
          />
        ) : null}
      </>
    );
  }

  render() {
    const { complete, visible } = this.getPageStatus();

    const {
      HasSeparateLivingCost: hasSeparateLivingCostConfig
    }: {
      [key: string]: ConfigField;
    } = this.props.config.Checklist.Finances.Page1;

    return (
      <Modal
        id={this.props.id}
        title="Provide details for your sponsorship"
        show={true}
        onClose={this.props.onClose}
        hideFooter={true}
      >
        {visible.orgType && (
          <RadioOptionsWithConfirm
            id="orgType"
            label="What type of organisation is sponsoring you?"
            questions={this.getOrganisationTypes(this.state.orgType)}
            onClick={this.onOrganisationTypeClick}
            onKeyDown={this.onOrganisationTypeKeyDown}
          />
        )}

        {visible.isMultiCountry && (
          <RadioOptionsWithConfirm
            id="isMultiCountry"
            label="Does the Organisation or Company have a presence in more than one country?"
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: this.state.isMultiCountry === true
              },
              {
                char: 'N',
                label: 'No',
                selected: this.state.isMultiCountry === false
              }
            ]}
            maxQuestions={2}
            maxWidth={300}
            isInline={true}
            inputHelperText={this.props.config.Checklist.Finances.Page1.CompanyPresence.helpText}
            onClick={this.onCountryPresenceClick}
            onKeyDown={this.onCountryPresenceKeyDown}
          />
        )}

        {visible.sponsorName && (
          <InputWithConfirm
            id="sponsorName"
            value={this.state.sponsorName}
            label="What is the name of your sponsor or sponsorship?"
            errorText={this.state.sponsorNameError}
            onChange={this.onSponsorNameChange}
            onBlur={(event: React.FormEvent<HTMLInputElement>) => {
              if (event.currentTarget.value === '') {
                this.setState({
                  sponsorNameError: 'Please enter the scholarship name'
                });
              }
            }}
            onFocus={() => {
              this.setState({
                sponsorNameError: ''
              });
            }}
          />
        )}

        {visible.fundingCover && (
          <FundingCoverSelect
            label="Select the costs that your sponsorship can be used towards:"
            subLabel="Your sponsorship details should specify which costs it will cover"
            fundingSummary={this.props.fundingSummary}
            sponsorshipCovers={this.state.sponsorshipCovers}
            onChange={(sponsorshipCovers: Array<string>) => {
              this.setState({ sponsorshipCovers });
            }}
          />
        )}

        {visible.courseForm && <>{this.renderCourse()}</>}

        {visible.livingForm && <>{this.renderLiving()}</>}

        {visible.courseAndLivingForm && (
          <>
            <RadioOptionsWithConfirm
              id="courseAndLivingFundSeparate"
              label={hasSeparateLivingCostConfig?.label}
              questions={[
                {
                  char: 'Y',
                  label: 'Yes',
                  selected: this.state.courseAndLivingFundSeparate === true
                },
                {
                  char: 'N',
                  label: 'No',
                  selected: this.state.courseAndLivingFundSeparate === false
                }
              ]}
              inputHelperText={hasSeparateLivingCostConfig?.helpText}
              onClick={(question: IQuestion) =>
                this.setState({
                  courseAndLivingFundSeparate: question.label === 'Yes'
                })
              }
              onKeyDown={(event: any, question: IQuestion) => {
                if (event.key === ' ' || event.key === 'Enter') {
                  this.setState({
                    courseAndLivingFundSeparate: question.label === 'Yes'
                  });
                }
              }}
            />
            {this.state.courseAndLivingFundSeparate === true && (
              <>
                {this.renderLiving()}
                {this.renderCourse()}
              </>
            )}
            {this.state.courseAndLivingFundSeparate === false && <>{this.renderCourseAndLiving()}</>}
          </>
        )}

        <Flex justifyContent="end">
          <Button
            id={'saveButton'}
            isDisabled={complete ? false : true}
            onClick={this.onSaveRegionDetails}
            size="lg"
            variant="solid"
          >
            {this.props.saveButtonText}
          </Button>
        </Flex>
      </Modal>
    );
  }
}

export default OrganisationSponsor;
